import { ButtonProps, LinearProgress, Typography, Box, Alert } from '@mui/material';
import { forwardRef, useImperativeHandle, useState } from 'react';

import { UploadStreamResponse } from '@/types/document';

import LoadingButton from '@/@mantis/components/@extended/LoadingButton';
import { UploadCloud01Icon } from '@/assets/icons/UploadCloud01Icon';
import { useDocumentUploadV2 } from '@/hooks/useDocumentUploadV2';

export interface API {
  click: () => void;
}

interface Props extends ButtonProps {
  documentId: string;
  documentUrl: string;
  locationIds?: number[];
  dataType?: string;
  onUploadComplete?: () => void;
}

const DocumentUploadV2Button = forwardRef<API, Props>(
  ({ documentId, documentUrl, locationIds, dataType, onUploadComplete, ...buttonProps }, ref) => {
    const [uploadStatus, setUploadStatus] = useState<string>('');
    const [uploadProgress, setUploadProgress] = useState<number>(0);
    const [error, setError] = useState<string>('');

    const { uploadDocument, isUploading, cancelUpload } = useDocumentUploadV2({
      onProgress: (response: UploadStreamResponse) => {
        setUploadStatus(response.message);
        if (response.progress !== undefined) {
          setUploadProgress(response.progress * 100);
        }
      },
      onComplete: (response: UploadStreamResponse) => {
        setUploadStatus(response.message);
        setUploadProgress(100);
        onUploadComplete?.();
      },
      onError: (errorMessage: string) => {
        setError(errorMessage);
        setUploadStatus('');
        setUploadProgress(0);
      },
    });

    const handleUpload = () => {
      setError('');
      setUploadStatus('Starting upload...');
      setUploadProgress(0);

      uploadDocument({
        document_id: documentId,
        url: documentUrl,
        data_type: dataType,
        location_ids: locationIds,
        user_id: '', // Will be set by the backend from JWT
      });
    };

    const handleCancel = () => {
      cancelUpload();
      setUploadStatus('');
      setUploadProgress(0);
      setError('');
    };

    useImperativeHandle(ref, () => ({
      click: handleUpload,
    }));

    return (
      <Box sx={{ width: '100%' }}>
        <LoadingButton
          {...buttonProps}
          color="primary"
          loadingPosition="start"
          loading={isUploading}
          size="medium"
          variant="contained"
          startIcon={<UploadCloud01Icon />}
          onClick={handleUpload}
          disabled={isUploading}
        >
          {isUploading ? 'Processing...' : 'Process with AI'}
        </LoadingButton>

        {isUploading && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {uploadStatus}
            </Typography>
            <LinearProgress
              variant={uploadProgress > 0 ? 'determinate' : 'indeterminate'}
              value={uploadProgress}
              sx={{ mb: 1 }}
            />
            <LoadingButton size="small" variant="outlined" onClick={handleCancel} sx={{ mt: 1 }}>
              Cancel
            </LoadingButton>
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Box>
    );
  }
);

export default DocumentUploadV2Button;
